import { useMemo, useState } from "react";
import { Clock, MapPin, Music, StickyNote, Users } from "lucide-react";
import {
  generateHourMarkers,
  getPositionForTime,
  computeBlockLayouts,
  getStageColor,
  CURRENT_TIME_COLOR,
} from "@produce/shared";

/** One other shooter on the same set. */
export interface TimelineCoAssignee {
  name: string;
  start: string | null;
  end: string | null;
}

/** A single schedule block, fully resolved by the caller. */
export interface TimelineItem {
  id: string;
  filmingStart: string;
  filmingEnd: string;
  displayName: string;
  stageLabel: string;
  isBreak: boolean;
  setStart: string | null;
  setEnd: string | null;
  note: string | null;
  coAssignees: TimelineCoAssignee[];
}

interface CrewScheduleTimelineProps {
  items: TimelineItem[];
  /** Show the live "now" marker (caller decides: today ∧ within event). */
  showCurrentTime: boolean;
  /** Current time "HH:MM". */
  currentTime: string;
  /** Hour used to anchor an otherwise-empty timeline. */
  fallbackHour: number;
  /**
   * Renders a time honouring the producer's 12h/24h schedule setting.
   * Required on purpose: a default would silently render 24h next to 12h
   * blocks — the bug this prop exists to prevent.
   */
  formatTime: (time: string) => string;
}

const LABEL_WIDTH = 48;
const CARD_LEFT = 56;
const MIN_HEIGHT = 44;
const CARD_GAP = 4;

/**
 * Vertical, time-proportional schedule for one crew member — the producer's
 * live-tracking view, styled to match the crew app. A 15-minute block is
 * visibly shorter than a 2-hour block, the current time is drawn as an overlay
 * marker, and the note / "also filming this" details are tucked behind icons
 * that expand the block on click (so the card stays compact by default).
 */
export function CrewScheduleTimeline({
  items,
  showCurrentTime,
  currentTime,
  fallbackHour,
  formatTime,
}: CrewScheduleTimelineProps) {
  /** "12:15" + "13:00" → "12:15 - 13:00" (or just the start when there's no end). */
  const range = (start: string | null, end: string | null) =>
    !start ? "" : end ? `${formatTime(start)} - ${formatTime(end)}` : formatTime(start);

  // Which blocks have their note / co-shooter panel expanded.
  const [openNotes, setOpenNotes] = useState<Record<string, boolean>>({});
  const [openCrew, setOpenCrew] = useState<Record<string, boolean>>({});

  // Layout (hour grid + per-block top/height) only depends on the items, so
  // memoize it — note/co-shooter expand toggles and the parent's 30s "now"
  // tick must not recompute the whole timeline.
  const { hours, totalHeight, layouts } = useMemo(() => {
    const normalized = items.map((i) => ({
      filming_start: i.filmingStart,
      filming_end: i.filmingEnd,
    }));
    const grid = generateHourMarkers(normalized, fallbackHour);
    const blocks = computeBlockLayouts(
      items.map((i) => ({ start: i.filmingStart, end: i.filmingEnd })),
      grid.hours,
      undefined,
      { minHeight: MIN_HEIGHT, gap: CARD_GAP },
    );
    return { hours: grid.hours, totalHeight: grid.totalHeight, layouts: blocks };
  }, [items, fallbackHour]);

  // Marker position tracks the live `currentTime` prop, so it stays outside the
  // layout memo.
  const markerTop = showCurrentTime ? getPositionForTime(currentTime, hours) : -1;

  const markerLine = (
    <div
      className="h-1 flex-1 rounded-full"
      style={{ backgroundColor: CURRENT_TIME_COLOR, boxShadow: `0 0 12px ${CURRENT_TIME_COLOR}` }}
    />
  );

  return (
    <div className="relative" style={{ height: totalHeight + 24 }}>
      {/* Hour gridlines + labels */}
      {hours.map((h) => (
        <div
          key={h.hour}
          className="pointer-events-none absolute flex items-start"
          style={{ top: h.topPosition, left: 0, right: 0 }}
        >
          <span
            className="text-xs font-medium text-muted-foreground"
            style={{ width: LABEL_WIDTH }}
          >
            {formatTime(h.time)}
          </span>
          <div className="mt-2 flex-1 border-t border-border/40" />
        </div>
      ))}

      {/* Schedule blocks */}
      {items.map((item, idx) => {
        const { top, height } = layouts[idx];
        const noteOpen = !!openNotes[item.id];
        const crewOpen = !!openCrew[item.id];
        const expanded = noteOpen || crewOpen;
        const stageColor = getStageColor(item.stageLabel || "");

        return (
          <div
            key={item.id}
            data-testid={`schedule-block-${item.id}`}
            className={`absolute rounded-xl border border-border bg-card px-3 py-2 shadow-sm ${
              item.isBreak ? "border-dashed" : ""
            } ${expanded ? "overflow-visible" : "overflow-hidden"}`}
            style={{
              top,
              left: CARD_LEFT,
              right: 8,
              height: expanded ? "auto" : height,
              minHeight: MIN_HEIGHT,
              borderLeftWidth: 4,
              borderLeftColor: stageColor,
              zIndex: expanded ? 30 : 10,
            }}
          >
            <div className="flex items-start justify-between gap-2">
              <h4 className="truncate text-sm font-semibold">{item.displayName}</h4>
              <div className="flex shrink-0 items-center gap-1">
                {item.note && (
                  <button
                    type="button"
                    aria-label="Show note"
                    aria-pressed={noteOpen}
                    onClick={() => setOpenNotes((s) => ({ ...s, [item.id]: !s[item.id] }))}
                    className={`rounded-md p-1 transition-colors hover:bg-muted ${
                      noteOpen ? "text-primary" : "text-muted-foreground"
                    }`}
                  >
                    <StickyNote className="h-4 w-4" />
                  </button>
                )}
                {item.coAssignees.length > 0 && (
                  <button
                    type="button"
                    aria-label="Show other shooters"
                    aria-pressed={crewOpen}
                    onClick={() => setOpenCrew((s) => ({ ...s, [item.id]: !s[item.id] }))}
                    className={`rounded-md p-1 transition-colors hover:bg-muted ${
                      crewOpen ? "text-primary" : "text-muted-foreground"
                    }`}
                  >
                    <Users className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              Film: {range(item.filmingStart, item.filmingEnd)}
            </div>

            {item.setStart && (
              <div className="mt-0.5 flex items-center gap-1.5 text-xs italic text-muted-foreground/80">
                <Music className="h-3.5 w-3.5 shrink-0" />
                Artist on stage {range(item.setStart, item.setEnd)}
              </div>
            )}

            {item.stageLabel &&
              item.stageLabel.toLowerCase() !== item.displayName.toLowerCase() && (
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  {item.stageLabel}
                </div>
              )}

            {noteOpen && item.note && (
              <div className="mt-2 whitespace-pre-line border-t border-border pt-2 text-xs text-muted-foreground">
                {item.note}
              </div>
            )}

            {crewOpen && item.coAssignees.length > 0 && (
              <div className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground">
                <p className="mb-1 font-medium">Also filming this</p>
                {item.coAssignees.map((c, i) => (
                  <p key={i}>
                    {c.name}
                    {c.start ? ` · ${range(c.start, c.end)}` : ""}
                  </p>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Live current-time marker — overlay on the timeline (mint, matches app). */}
      {showCurrentTime && markerTop >= 0 && (
        <div
          data-testid="current-time-marker"
          className="pointer-events-none absolute flex items-center gap-2"
          style={{
            top: markerTop,
            left: LABEL_WIDTH,
            right: 8,
            transform: "translateY(-50%)",
            zIndex: 40,
          }}
        >
          {markerLine}
          <span
            className="rounded-full px-2 py-0.5 text-xs font-bold"
            style={{
              color: CURRENT_TIME_COLOR,
              backgroundColor: "rgba(167,243,208,0.2)",
              border: "1px solid rgba(167,243,208,0.4)",
            }}
          >
            {formatTime(currentTime)}
          </span>
          {markerLine}
        </div>
      )}
    </div>
  );
}
